import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, CTAButton, SectionLabel } from "@/components/common/Bits";
import { Reveal, WindowGrid } from "@/components/motion/Reveal";
import { Spinner } from "@/components/common/States";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { getServiceBySlug } from "@/services/api";
import { SERVICES } from "@/data/services";
import { SERVICE_FAQS } from "@/data/mockData";
import { useProjects } from "@/hooks/useContent";
import { waLink, WA_MESSAGES } from "@/data/company";

export default function ServiceDetail({ base = "services" }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const { data: projects } = useProjects();

    useEffect(() => {
        let active = true;
        setLoading(true);
        getServiceBySlug(slug).then((s) => {
            if (!active) return;
            setService(s);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center pt-24">
                <Spinner />
            </div>
        );
    }

    if (!service) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 pt-24 text-center">
                <h1 className="font-display text-3xl font-semibold text-brand-blue">Service not found</h1>
                <CTAButton onClick={() => navigate("/services")}>Back to Services</CTAButton>
            </div>
        );
    }

    const waMsg = WA_MESSAGES[slug] || WA_MESSAGES.general;
    const related = projects
        .filter((p) =>
            String(p.category || "")
                .toLowerCase()
                .includes(service.name.split(" ")[0].toLowerCase()),
        )
        .slice(0, 3);
    const relatedServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
    const process = service.process || null;

    return (
        <>
            <SEO title={service.seoTitle || service.name} path={`/${base}/${slug}`} description={service.intro} image={service.image} />
            <PageHero label={base === "special-solutions" ? "Other Solution" : "Service"} title={service.name} subtitle={service.short} image={service.image} />

            {/* Intro + image */}
            <section className="py-24">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] rounded-tr-[80px] shadow-brand-lg">
                            <img src={service.image} alt={`ProCare.SG ${service.name}`} className="aspect-[4/3] w-full object-cover" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <SectionLabel>Overview</SectionLabel>
                        <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-brand-blue sm:text-4xl">About our {service.name} service</h2>
                        <p className="mt-6 text-base leading-relaxed text-brand-ink/70">{service.intro}</p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {service.forWhom?.map((f) => (
                                <span key={f} className="rounded-full bg-brand-surface px-4 py-1.5 text-xs font-semibold text-brand-blue">{f}</span>
                            ))}
                        </div>
                        <div className="mt-8">
                            <CTAButton href={waLink(waMsg)} whatsapp testid="service-whatsapp-cta">Get a Free Quote</CTAButton>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Scope + use cases */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2">
                    <Reveal>
                        <SectionLabel>Scope of work</SectionLabel>
                        <h3 className="mt-5 font-display text-2xl font-semibold text-brand-blue">What’s included</h3>
                        <ul className="mt-6 space-y-3">
                            {service.scope?.map((item) => (
                                <li key={item} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-brand">
                                    <WindowGrid size={16} className="mt-1 shrink-0" />
                                    <span className="text-sm text-brand-ink/80">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <SectionLabel>Common use cases</SectionLabel>
                        <h3 className="mt-5 font-display text-2xl font-semibold text-brand-blue">When to call us</h3>
                        <ul className="mt-6 space-y-3">
                            {service.useCases?.map((item) => (
                                <li key={item} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-brand">
                                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                                    <span className="text-sm text-brand-ink/80">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* Offerings (e.g. Events Set Up sub-services) */}
            {service.offerings?.length > 0 && (
                <section className="py-24">
                    <div className="mx-auto max-w-7xl px-6">
                        <Reveal className="max-w-2xl">
                            <SectionLabel>What we offer</SectionLabel>
                            <h3 className="mt-5 font-display text-3xl font-semibold text-brand-blue">Our {service.name.toLowerCase()} services</h3>
                        </Reveal>
                        <div className="mt-12 grid gap-6 md:grid-cols-3">
                            {service.offerings.map((o, i) => (
                                <Reveal key={o.title} delay={i * 0.08}>
                                    <div className="flex h-full flex-col rounded-[24px] rounded-tr-[48px] border border-brand-blue/10 bg-white p-7 shadow-brand">
                                        <span className="font-display text-4xl font-bold text-brand-blue/10">{String(i + 1).padStart(2, "0")}</span>
                                        <h4 className="mt-3 font-display text-xl font-semibold text-brand-blue">{o.title}</h4>
                                        <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-ink/65">{o.desc}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Process (special solutions) */}
            {process && (
                <section className="py-24">
                    <div className="mx-auto max-w-7xl px-6">
                        <Reveal className="max-w-2xl">
                            <SectionLabel>Our process</SectionLabel>
                            <h3 className="mt-5 font-display text-3xl font-semibold text-brand-blue">How we handle it, step by step</h3>
                        </Reveal>
                        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {process.map((step, i) => (
                                <Reveal key={step} delay={i * 0.05}>
                                    <div className="h-full rounded-3xl border border-brand-blue/10 bg-white p-6">
                                        <span className="font-display text-4xl font-bold text-brand-blue/10">{String(i + 1).padStart(2, "0")}</span>
                                        <p className="mt-2 text-sm font-medium text-brand-ink/80">{step}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        {service.disclaimer && (
                            <p className="mt-8 rounded-2xl bg-brand-surface p-5 text-sm text-brand-ink/60">{service.disclaimer}</p>
                        )}
                    </div>
                </section>
            )}

            {/* Benefits */}
            <section className={`${process ? "bg-brand-surface" : ""} py-24`}>
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-2xl">
                        <SectionLabel>Benefits</SectionLabel>
                        <h3 className="mt-5 font-display text-3xl font-semibold text-brand-blue">Why choose ProCare.SG</h3>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {service.benefits?.map((b, i) => (
                            <Reveal key={b} delay={i * 0.05}>
                                <div className="h-full rounded-3xl bg-white p-6 shadow-brand">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-red/10 text-brand-red">
                                        <Check className="h-5 w-5" />
                                    </div>
                                    <p className="mt-4 text-sm font-medium text-brand-ink/80">{b}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Related projects */}
            {related.length > 0 && (
                <section className="py-24">
                    <div className="mx-auto max-w-7xl px-6">
                        <Reveal className="max-w-2xl">
                            <SectionLabel>Related works</SectionLabel>
                            <h3 className="mt-5 font-display text-3xl font-semibold text-brand-blue">Recent {service.name} projects</h3>
                        </Reveal>
                        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {related.map((p, i) => (
                                <Reveal key={p.id} delay={i * 0.06}>
                                    <Link to={`/our-works/${p.slug}`} className="group block overflow-hidden rounded-3xl bg-white shadow-brand transition-all hover:-translate-y-1 hover:shadow-brand-lg">
                                        <div className="aspect-[4/3] overflow-hidden">
                                            <img src={p.featuredImage} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        </div>
                                        <div className="p-5">
                                            <h4 className="font-display text-lg font-semibold text-brand-blue">{p.title}</h4>
                                            <p className="mt-1 text-xs uppercase tracking-widest text-brand-ink/65">{p.propertyType} · {p.location}</p>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FAQ */}
            <section className="bg-brand-surface py-24">
                <div className="mx-auto max-w-3xl px-6">
                    <Reveal>
                        <SectionLabel>FAQ</SectionLabel>
                        <h3 className="mt-5 font-display text-3xl font-semibold text-brand-blue">Frequently asked questions</h3>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-8">
                        <Accordion type="single" collapsible className="space-y-3">
                            {SERVICE_FAQS.map((f, i) => (
                                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-brand-blue/10 bg-white px-5">
                                    <AccordionTrigger className="text-left font-semibold text-brand-blue hover:no-underline">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-brand-ink/70">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* Related services + CTA */}
            <section className="py-24">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="rounded-[32px] bg-brand-blue p-10 text-white grain sm:p-14">
                        <h3 className="max-w-2xl font-display text-3xl font-semibold sm:text-4xl">Ready to book your {service.name.toLowerCase()}?</h3>
                        <p className="mt-4 max-w-xl text-white/70">Message us on WhatsApp or send an enquiry and we’ll get back to you with a clear quotation.</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <CTAButton href={waLink(waMsg)} whatsapp testid="service-cta-whatsapp">WhatsApp Us</CTAButton>
                            <CTAButton to="/contact" variant="light" testid="service-cta-contact">Request a Quotation</CTAButton>
                        </div>
                    </Reveal>

                    <div className="mt-16">
                        <h4 className="font-display text-xl font-semibold text-brand-blue">Related services</h4>
                        <div className="mt-6 flex flex-wrap gap-3">
                            {relatedServices.map((s) => (
                                <Link key={s.slug} to={`/services/${s.slug}`} className="rounded-full border border-brand-blue/15 px-5 py-2.5 text-sm font-medium text-brand-blue transition-colors hover:border-brand-red hover:text-brand-red">
                                    {s.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
